import type {ErrorContext} from '@github-ui/failbot'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'

import {ERROR_BOUNDARY_METADATA, type ErrorBoundaryMetadata} from './ErrorBoundary'

/**
 * Options for creating React root error handlers.
 */
export interface ReactRootErrorHandlerOptions {
  /**
   * The name of the React application, used for error reporting context.
   */
  appName: string
}

/**
 * Props that may be present on an error boundary component.
 * This is a subset of ErrorBoundaryProps that doesn't require children,
 * since React's errorInfo.errorBoundary.props type doesn't include children.
 */
interface ErrorBoundaryLikeProps {
  critical?: boolean
  boundaryName?: string
  onError?: (error: Error, context?: ErrorContext) => void
}

/**
 * The error info object passed to React 19's error callbacks.
 * This type is compatible with React's internal typing while allowing
 * access to our ErrorBoundary-specific props.
 */
export interface ReactErrorInfo {
  componentStack?: string
  errorBoundary?: {
    props?: ErrorBoundaryLikeProps
    constructor?: {
      name?: string
    }
  }
}

/**
 * Creates the `onCaughtError` callback for React 19's `createRoot`/`hydrateRoot`.
 *
 * This callback is invoked when an error is caught by an Error Boundary.
 * It handles reporting to Sentry while respecting:
 * - Errors with `shouldSkipReport` flag (e.g., ES being down)
 * - Error Boundaries with custom `onError` handlers (they handle their own reporting)
 * - Critical vs non-critical error classification
 *
 * @param options - Configuration for the error handler
 * @returns A callback suitable for React 19's `onCaughtError` option
 *
 * @example
 * ```ts
 * import { createOnCaughtError, createOnUncaughtError } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const root = createRoot(container, {
 *   onCaughtError: createOnCaughtError({ appName: 'my-app' }),
 *   onUncaughtError: createOnUncaughtError({ appName: 'my-app' }),
 * })
 * ```
 */
export function createOnCaughtError(options: ReactRootErrorHandlerOptions) {
  const {appName} = options

  return (error: unknown, errorInfo: ReactErrorInfo) => {
    // Try to read props from the error boundary instance
    // Note: errorInfo.errorBoundary is the React component instance
    const boundaryProps = errorInfo.errorBoundary?.props

    // Also check for metadata attached to the error (fallback for componentDidCatch)
    const errorMetadata = (error as Error & {[ERROR_BOUNDARY_METADATA]?: ErrorBoundaryMetadata})[
      ERROR_BOUNDARY_METADATA
    ]

    // Prefer props from boundary instance, fall back to error metadata
    const critical = boundaryProps?.critical ?? errorMetadata?.critical ?? false
    const boundaryName =
      boundaryProps?.boundaryName ?? errorMetadata?.boundaryName ?? errorInfo.errorBoundary?.constructor?.name
    const hasOnError = typeof boundaryProps?.onError === 'function'

    // Skip reporting if:
    // 1. Error has shouldSkipReport flag (e.g., ES being down)
    // 2. Boundary has a custom onError handler (it handles reporting)
    if (error && typeof error === 'object' && 'shouldSkipReport' in error && error.shouldSkipReport) {
      return
    }
    if (hasOnError) {
      return
    }

    reportError(error, {
      critical,
      reactAppName: appName,
      reactErrorBoundaryName: boundaryName,
    })

    // Log in development for debugging
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Error caught by boundary:', error)
      if (errorInfo.componentStack) {
        // eslint-disable-next-line no-console
        console.warn('componentStack', errorInfo.componentStack)
      }
    }
  }
}

/**
 * Creates the `onUncaughtError` callback for React 19's `createRoot`/`hydrateRoot`.
 *
 * This callback is invoked when an error is thrown and NOT caught by any Error Boundary.
 * These are critical errors that will crash the app.
 *
 * @param options - Configuration for the error handler
 * @returns A callback suitable for React 19's `onUncaughtError` option
 *
 * @example
 * ```ts
 * import { createOnCaughtError, createOnUncaughtError } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const root = createRoot(container, {
 *   onCaughtError: createOnCaughtError({ appName: 'my-app' }),
 *   onUncaughtError: createOnUncaughtError({ appName: 'my-app' }),
 * })
 * ```
 */
export function createOnUncaughtError(options: ReactRootErrorHandlerOptions) {
  const {appName} = options

  return (error: unknown, errorInfo: ReactErrorInfo) => {
    // This is called when an error is thrown and NOT caught by any Error Boundary.
    // These are critical errors that will crash the app.
    reportError(error, {
      critical: true,
      reactAppName: appName,
    })

    // Log in development for debugging
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Error not caught by boundary:', error)
      if (errorInfo.componentStack) {
        // eslint-disable-next-line no-console
        console.warn('componentStack', errorInfo.componentStack)
      }
    }
  }
}

/**
 * Creates both error callbacks for React 19's `createRoot`/`hydrateRoot`.
 *
 * This is a convenience function that returns both `onCaughtError` and `onUncaughtError`
 * callbacks configured for the given app name.
 *
 * @param options - Configuration for the error handlers
 * @returns An object containing both `onCaughtError` and `onUncaughtError` callbacks
 *
 * @example
 * ```ts
 * import { createReactRootErrorHandlers } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const errorHandlers = createReactRootErrorHandlers({ appName: 'my-app' })
 * const root = createRoot(container, errorHandlers)
 * ```
 */
export function createReactRootErrorHandlers(options: ReactRootErrorHandlerOptions): {
  onCaughtError: (error: unknown, errorInfo: ReactErrorInfo) => void
  onUncaughtError: (error: unknown, errorInfo: ReactErrorInfo) => void
} {
  return {
    onCaughtError: createOnCaughtError(options),
    onUncaughtError: createOnUncaughtError(options),
  }
}
